import { useEffect, useRef, useState } from 'react';
import { STAGES } from '@app/constants/stages';
import { Recording } from '../Recording';
import { Answer } from '../../Answer';
import { WaitingTime } from '../WaitingTime';
import * as S from './SpeakingReadAloud.styles';
import VoiceIndicator from '../Recording/VoiceIndicator';
import { useDispatch, useSelector } from 'react-redux';
import { setAllowNext } from '@app/store/slices/mockTestSlice';
import { RootState } from '@app/store/store';

interface Props {
  waitingTime: number;
  recordingTime: number;
  showAnswer: boolean;
  text: string;
  questionNumber: string;
  canStart: boolean;
  onAnswerChange?: (audio: Blob | string) => void;
}

const SpeakingReadAloud: React.FC<Props> = ({
  waitingTime,
  recordingTime,
  showAnswer,
  text,
  questionNumber,
  canStart,
  onAnswerChange,
}) => {
  const dispatch = useDispatch();
  const { isCompleteAudio } = useSelector((state: RootState) => state.mockTest);

  const recordingRef = useRef<{ startRecording: () => void; stopRecording: () => void }>(null);
  const [stage, setStage] = useState(STAGES.START);
  const [audioURL, setAudioURL] = useState('');
  const [audioBlod, setAudioBlod] = useState<Blob>();
  const waitingTimeRef = useRef<{ finishImmediately: () => void } | null>(null);

  useEffect(() => {
    setStage(STAGES.COMPLETE);
    handleStopRecording();
    setStage(STAGES.START);
  }, [questionNumber]);

  useEffect(() => {
    if (!showAnswer) return;
    if (stage == STAGES.START) {
      setStage(STAGES.COMPLETE);
    } else {
      handleStopRecording();
    }
  }, [showAnswer]);

  useEffect(() => {
    if (!isCompleteAudio) return;
    handleStopRecording();
    // onAnswerChange && onAnswerChange('');
  }, [isCompleteAudio]);

  const handleStart = () => {
    if (stage === STAGES.RECORDING) {
      handleStopRecording();
      return;
    }
    dispatch(setAllowNext(true));
    waitingTimeRef.current?.finishImmediately();
    setStage(STAGES.RECORDING);
    recordingRef.current?.startRecording();
  };

  const handleComplete = async (audio: string) => {
    setStage(STAGES.COMPLETE);
    setAudioURL(audio);

    const blob = await fetch(audio).then((res) => res.blob());
    setAudioBlod(blob);
    onAnswerChange && onAnswerChange(blob);
  };

  const handleStopRecording = () => {
    recordingRef.current?.stopRecording();
  };

  return (
    <>
      <S.Box>
        <div className="flex">
          <WaitingTime ref={waitingTimeRef} onStart={handleStart} waitingTime={waitingTime} key={questionNumber} />
          <S.VoiceIndicator>{stage === STAGES.RECORDING && <VoiceIndicator isRecording={true} />}</S.VoiceIndicator>
        </div>

        <Recording
          ref={recordingRef}
          recordingTime={recordingTime}
          onComplete={handleComplete}
          onStartRecording={handleStart}
          canStart={canStart}
        />
      </S.Box>
      <div className="mt-5">{text}</div>

      <div className="mt-5">
        {showAnswer && (
          <Answer
            showAnswer={showAnswer}
            audioURL={audioURL}
            showNote={true}
            gradingContent={text}
            questionId={questionNumber}
            audioBlod={audioBlod}
          />
        )}
      </div>
    </>
  );
};

export default SpeakingReadAloud;
